import { useCallback, useEffect, useMemo, useState } from "react";
import { ElasticProduct } from "@usereactify/search";
import axios from "axios";

import type {
  Cart,
  CartUpdateResult,
  GiftProduct,
  GQLProduct,
  LineItem,
  NormalisedProduct,
  Product,
} from "@app/scripts/types";
import { formatPrice, updateCart, addToBackInStock, addToCart, extractIdFromGid, stripColourFromVariantTitle } from "@app/scripts/utils";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_PRODUCTS_BY_FILTER, GET_PRODUCTS_BY_FILTER_MULTIPLE, GET_PRODUCTS_BY_HANDLE, GET_PRODUCTS_BY_ID } from "@app/scripts/utils/queries";
import config from "@app/config";
import { isKids } from "../utils/tagFilters";
import { useLocalisation } from "@app/scripts/react-hooks/localisation";

export const useShopifyCart = ({ giftProduct }: { giftProduct?: GiftProduct }) => {
  const { localisation } = useLocalisation();
  const { normaliseProduct } = useShopify();
  const [products, setProducts] = useState<NormalisedProduct[]>([]);
  const [cart, setCart] = useState<Cart | null>(null);
  const [oosItems, setOosItems] = useState<number[]>([]); // array of oos variant ids
  const [error, setError] = useState<string | undefined | null>(null);
  const [gifts, setGifts] = useState<NormalisedProduct[]>([])
  const [giftsQueried, setGiftsqueried] = useState(false)

  const forceGlobalUpdateEvent = new Event("forceGlobalUpdate", { bubbles: true });

  const queryRelated = async (giftsToAdd) => {
    if (!giftsToAdd) return []

    const queries = giftsToAdd.map(product => {
      // "style--" is a legacy tag for backwards compatibility purposes, can be removed post 08/2025
      const styleTag = product.tags.filter((tag) => tag.includes("altCode--") || tag.includes("style--"));
      // there is a link between parent--XXXX-rest of handle and sku-XXXX
      const parentToSku = product.tags
        .filter((tag) => tag.includes("size--") || tag.includes("parent--"))
        ?.map(t => t.replace("parent--", "sku--")
          .replace("size--", "sku--")
          .match(/sku--([^ -]+)/)[1]);
      if (!styleTag?.length && !parentToSku.length) return
      return [...styleTag, ...parentToSku].map(tag => `tag:${tag}`).join(" OR ")
    })?.filter(Boolean) as string[]

    if (!queries?.length) return []

    const withRelated = await shopify(GET_PRODUCTS_BY_FILTER, {
      limit: 250,
      filter: queries.join(" OR "),
    });

    return withRelated?.products?.nodes || []
  }

  useEffect(() => {
    if (!giftProduct?.gift_products?.length) return
    // In the case of industrie kids, the size variants can be different products so we need to query
    // the related ones and add them to the gifts state
    const getRelatedGifts = async () => {
      const giftsToAdd = giftProduct?.gift_products?.map((product) => normaliseProduct(product))
      if (!giftsToAdd) return

      const withRelated = (await queryRelated(giftsToAdd)).filter(Boolean)
      // the kids product structure requires to query twice to get everything
      const definetlyWithRelated = (await queryRelated(withRelated)).filter(Boolean)

      if (definetlyWithRelated?.length) {
        // All gifts need to be availble to change between products, but
        // we need to hide the additionals from the cart so it doesn't look messy
        // we can add an idenetifer for this
        setGifts(definetlyWithRelated.map(node => ({
          ...normaliseProduct(node),
          id: parseInt(extractIdFromGid(node.id)),
          hide_in_carousel: !giftsToAdd.find(g => g.id === parseInt(extractIdFromGid(node.id)))
        })))
        setGiftsqueried(true)
      } else {
        setGifts(giftsToAdd)
        setGiftsqueried(true)
      }
    }

    if (gifts?.length && isKids() || Shopify.shop === "industrie-kids.myshopify.com") {
      getRelatedGifts()
      // setGifts(giftProduct?.gift_products?.map((product) => normaliseProduct(product)))
    } else {
      setGifts(giftProduct?.gift_products?.map((product) => normaliseProduct(product)))
    }
  }, [giftProduct?.gift_products])

  const giftIds = useMemo(() => (gifts?.length && gifts?.map((gift) => gift.id)) || [], [gifts]);

  const giftsInCart = useMemo(
    () => cart?.items?.filter((lineItem) => lineItem?.properties?.[config?.attributes?.isGwp] === "true") || [],
    [cart?.items, config?.attributes?.isGwp]
  );

  const totalGiftPrice = useMemo(
    () =>
      giftsInCart?.reduce((acc: number, giftLine: LineItem) => {
        return (acc += giftLine.final_price / 100);
      }, 0),
    [giftsInCart]
  );

  const cartTotal = useMemo(() => {
    if (!cart) return 0;
    const subtotal = cart.items_subtotal_price / 100;

    return subtotal - totalGiftPrice;
  }, [cart?.items_subtotal_price, totalGiftPrice]);

  const totalDiscount = useMemo(() => {
    let td = 0;
    cart?.items?.forEach((item) => {
      const matchingProduct = products.find((p) => p.id === item.product_id);
      const matchingVariant = matchingProduct?.variants?.find((v) => v.id === item.variant_id);
      if (matchingVariant)
        td += matchingVariant?.compare_at_price
          ? (matchingVariant.compare_at_price - matchingVariant.price) * item.quantity
          : 0;
    });
    return td > 0 ? formatPrice(td, localisation?.currency_code) : null;
  }, [cart?.items, products, localisation]);

  const cartBeforeDiscountPrice = useMemo(() => {
    let td = 0;
    cart?.items?.forEach((item) => {
      const matchingProduct = products.find((p) => p.id === item.product_id);
      const matchingVariant = matchingProduct?.variants?.find((v) => v.id === item.variant_id);
      if (matchingVariant)
        td += matchingVariant?.compare_at_price
          ? (matchingVariant.compare_at_price - matchingVariant.price) * item.quantity
          : 0;
    });
    return formatPrice(cartTotal + td, localisation?.currency_code);
  }, [cart?.items, products, localisation]);

  const addOosVariant = useCallback(
    (variantId: number) => {
      if (!oosItems?.includes(variantId)) {
        setOosItems([...(oosItems || []), variantId]);
      }
    },
    [setOosItems, oosItems]
  );

  const localGiftThreshold = cart?.currency ? giftProduct?.gift_threshold?.[cart.currency] ?? giftProduct?.gift_threshold?.["DEFAULT"] * Shopify.currency.rate : giftProduct?.gift_threshold?.["DEFAULT"] * Shopify.currency.rate;
  const overGiftThreshold = !!(localGiftThreshold && cartTotal > localGiftThreshold);
  const untilGiftThreshold = {
    amount: localGiftThreshold ? localGiftThreshold - cartTotal : 0,
    currency: cart?.currency || ""
  }

  const fetchCart = useCallback(async () => {
    try {
      const cart = (await axios.get("/cart.json"))?.data;
      const products = await shopify(GET_PRODUCTS_BY_ID, {
        ids: cart?.items?.map((item: any) => `gid://shopify/Product/${item?.product_id}`),
      });
      const normalisedProducts = products?.nodes?.map((product: Product) => {
        const result = normaliseProduct(product);
        return {
          ...result,
          id: Number(result?.id?.split("/")[4]),
          variants: result?.variants?.map((v: any) => ({
            ...v,
            id: Number(v?.id?.split("/")[4]),
          })),
        };
      });
      setCart(cart);
      setProducts(normalisedProducts);
    } catch (err) {
      console.error(err);
    }
  }, [setCart, setProducts, normaliseProduct]);

  const handleAdd = useCallback(
    async ({
      variantId,
      qty,
      refresh = true,
      properties = null,
    }: {
      variantId: number;
      qty: number;
      refresh?: boolean;
      properties?: { [key: string]: string } | null;
    }) => {
      setError(null);
      const result = await addToCart({ id: variantId, qty, properties });

      if (refresh) {
        window.dispatchEvent(forceGlobalUpdateEvent);
      }
      if (result.status === 200) {
        return { success: true } as CartUpdateResult;
      } else if (result?.message) {
        const res: CartUpdateResult = { error: result.message as string };
        setError(res.error);
        return res;
      } else {
        const res: CartUpdateResult = { error: "There was an error adding to the cart." };
        setError(res.error);
        return res;
      }
    },
    [fetchCart, updateCart]
  );

  const handleUpdate = useCallback(
    async (variantIdOrKey: number | string, qty: number, refresh = true) => {
      setError(null);
      const result = await updateCart({ id: variantIdOrKey, qty: qty });

      if (refresh) {
        window.dispatchEvent(forceGlobalUpdateEvent);
      }
      if (result.status === 200) {
        return { success: true } as CartUpdateResult;
      } else if (result?.message) {
        const res: CartUpdateResult = { error: result.message as string };
        setError(res.error);
        return res;
      } else {
        const res: CartUpdateResult = { error: "There was an error updating the cart." };
        setError(res.error);
        return res;
      }
    },
    [fetchCart, updateCart]
  );

  const addGift = useCallback(
    async (gift?: NormalisedProduct) => {
      if (!gift) return;
      const firstGiftVariant = gift.variants?.find((v) => v.available);

      if (!firstGiftVariant) return;

      if (!giftsInCart.length && overGiftThreshold && firstGiftVariant) {
        await handleAdd({
          variantId: firstGiftVariant.id,
          qty: 1,
          refresh: true,
          properties: {
            [config?.attributes?.isGwp]: "true",
          },
        });
      } // remove gifts
    },
    [giftsInCart, overGiftThreshold, config?.attributes?.isGwp]
  );

  const autoAddRemove = async () => {
    // remove any gifts that shouldn't be in the cart (gifts that are no longer in the customiser or GWP that are in the cart and the size-- tag doesn't match the parent gift)
    const giftThatShouldntBeInCart = giftsInCart?.find((gift) => !giftIds?.includes(gift?.product_id));
    if (giftThatShouldntBeInCart) await handleUpdate(giftThatShouldntBeInCart.key, 0, true);

    // auto remove all gifts if under threshold
    if (!overGiftThreshold) {
      for (const gift of giftsInCart) {
        await handleUpdate(gift.key, 0, true);
        const selectedGiftKey = `selected_gift-${gift.product_id}`;
        sessionStorage.removeItem(selectedGiftKey);
      }
    }

    // auto add gift if its singular
    if (gifts?.length === 1 && overGiftThreshold) {
      if (gifts?.[0]?.variants?.length && gifts?.[0]?.variants?.length === 1) {
        // dont add gifts that are manually removed
        const manuallyRemoved = gifts?.length === 1 && sessionStorage.getItem(`gift_removed:${gifts[0].id}`) === "true";

        if (!manuallyRemoved && giftsInCart.length === 0) {
          await addGift(gifts[0]);
        }
      }
    }
  };

  useEffect(() => {
    if ((isKids() && giftsQueried) || !isKids()) {
      autoAddRemove();
    }
  }, [overGiftThreshold, giftsInCart, giftsQueried, isKids]);

  const goToCheckout = useCallback(
    async (e: any) => {
      e.preventDefault?.();

      if (cart?.items) {
        for (const item of cart?.items) {
          if (oosItems.includes(item.variant_id)) {
            await updateCart({ id: item.variant_id, qty: 0 });
          }
        }
      }
      window.location.href = "/checkout";
    },
    [cart?.items, oosItems, updateCart]
  );

  const onTabVisible = useCallback(async () => {
    if (document.visibilityState == "visible") {
      fetchCart();
    }
  }, [fetchCart, cart]);

  useEffect(() => {
    window.addEventListener("forceGlobalUpdate", fetchCart);
    document.addEventListener("visibilitychange", onTabVisible);

    return () => {
      window.removeEventListener("forceGlobalUpdate", fetchCart);
      document.removeEventListener("visibilitychange", onTabVisible);
    };
  }, []);

  return {
    products,
    cart,
    fetchCart,
    goToCheckout,
    handleUpdate,
    handleAdd,
    totalDiscount,
    addOosVariant,
    cartTotal,
    oosItems,
    giftIds,
    giftMessage: giftProduct?.gift_message,
    cartBeforeDiscountPrice,
    error,
    setError,
    gifts,
    giftsInCart,
    overGiftThreshold,
    untilGiftThreshold,
  };
};

export const useShopifyProduct = ({
  product,
  noDefault = false,
}: {
  product?: NormalisedProduct;
  noDefault?: boolean;
}) => {
  const [error, setError] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | number | null>(
    noDefault ? null : product?.variants?.[0]?.id
  );

  const title = product?.title?.substr(0, product?.title.lastIndexOf("-"));

  const getColor = (p?: NormalisedProduct) => {
    const arr = p?.title?.split("-");
    return arr?.length ? arr.slice(-1) : null;
  };

  const getColorFromTitle = (p?: NormalisedProduct) => {
    if (!p) return "";

    return p.title.split(" - ")[1] || "";
  };

  const getBadges = (p?: NormalisedProduct) =>
    p?.tags
      ?.filter((tag) => tag.includes("flag--"))
      ?.map((tag) => ({
        tag: tag.toLowerCase(),
        title: tag.replace("flag--", ""),
      }));

  const colorFromTitle = useMemo(() => getColorFromTitle(product), [product?.title, getColorFromTitle]);

  const color = useMemo(() => getColor(product), [product?.tags, getColor]);

  const badges = useMemo(() => getBadges(product), [product?.tags, getBadges]);

  const url = `/products/${product?.handle}`;

  const handleBISSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const email = data.get("email") as string;
    const variantId = parseInt(data?.get("variant") as string);
    const res = await addToBackInStock({ email, variantId });
  };

  const handleChangeVariant = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      setError(null);
      setSelectedVariantId(e.target.value);
      return e.target.value;
    },
    [setSelectedVariantId, product?.variants, setError, selectedVariantId]
  );

  const handleAddToCart = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement>,
      variant_id: number | null = null,
      properties: { [key: string]: string } | null = null
    ) => {
      e.preventDefault();
      setError(null);

      if (!variant_id && !selectedVariantId) {
        setError("Please select a size.");
        return false;
      }

      const res = await addToCart({
        id: variant_id ?? selectedVariantId,
        qty: 1,
        properties,
      });

      if (res.status === 200) {
        return true;
      } else {
        setError((res?.description as string) ?? "There was an error adding to the cart");
        return false;
      }
    },
    [addToCart, selectedVariantId, setError]
  );

  return {
    handleAddToCart,
    setSelectedVariantId,
    handleChangeVariant,
    selectedVariantId,
    handleBISSubmit,
    getColor,
    color,
    getColorFromTitle,
    colorFromTitle,
    getBadges,
    badges,
    error,
    url,
    setError,
    title,
  };
};

export const useShopify = () => {
  const normaliseElasticProduct: (product: ElasticProduct) => NormalisedProduct = (product) => ({
    is_normalised: true,
    available: true,
    compare_at_price: product.discount_amount ? product.price_min + product.discount_amount : null,
    compare_at_price_min: product.discount_amount ? product.price_min + product.discount_amount : null,
    compare_at_price_max: product.discount_amount ? product.price_max + product.discount_amount : null,
    description: product.description,
    featured_image: {
      id: product.images?.[0]?.src,
      src: product.images?.[0]?.src,
      alt: product.images?.[0]?.alt,
    },
    handle: product.handle,
    id: product.id.toString(),
    media: product.images?.map((img) => ({
      id: img?.src,
      src: img?.src,
      alt: img?.alt,
    })),
    price: product.price_min,
    price_min: product.price_min,
    price_max: product.price_max,
    tags: product.tags,
    title: product.title,
    variants: product.variants?.map((variant) => ({
      title: stripColourFromVariantTitle(variant.title),
      available: variant.available,
      compare_at_price: variant.compare_at_price,
      currentlyNotInStock: variant.inventory_quantity === 0 && variant.available === false,
      id: variant.id,
      price: variant.price,
      presentment_prices: variant.presentment_prices?.map(
        (p) =>
        ({
          compare_at_price: p.compare_at_price?.amount,
          price: p.price.amount,
          currency_code: p.price.currency_code,
        } || [])
      ),
    })),
  });

  const normaliseProduct: (product: Product | GQLProduct) => NormalisedProduct = (product) => {
    const isShopifyJson = !!(product as Product)?.price;

    if (isShopifyJson) {
      return {
        ...product,
        is_normalised: true,
        price: (product as Product).price / 100,
        compare_at_price: (product as Product)?.compare_at_price ? (product as Product).compare_at_price / 100 : null,
        media: (product as Product)?.media?.map((m) => ({
          alt: m.alt,
          height: m.height,
          width: m.width,
          src: m.src,
          id: m.id,
        })),
        variants: (product as Product).variants?.map((variant) => ({
          available: variant.available,
          compare_at_price: variant.compare_at_price ? variant.compare_at_price / 100 : null,
          currentlyNotInStock: "check_via_gql",
          id: variant.id,
          price: variant.price / 100,
          title: stripColourFromVariantTitle(variant.title),
        })),
      };
    }

    return {
      is_normalised: true,
      available: (product as GQLProduct)?.availableForSale,
      compare_at_price: parseFloat((product as GQLProduct)?.compareAtPriceRange?.minVariantPrice.amount),
      compare_at_price_min: parseFloat((product as GQLProduct)?.compareAtPriceRange?.minVariantPrice.amount),
      compare_at_price_max: parseFloat((product as GQLProduct)?.compareAtPriceRange?.maxVariantPrice.amount),
      description: (product as GQLProduct)?.descriptionHtml,
      productType: (product as GQLProduct)?.productType,
      featured_image: {
        alt: (product as GQLProduct)?.images?.edges?.[0]?.node.altText,
        height: (product as GQLProduct)?.images?.edges?.[0]?.node.height,
        width: (product as GQLProduct)?.images?.edges?.[0]?.node.width,
        src: (product as GQLProduct)?.images?.edges?.[0]?.node.url,
        id: (product as GQLProduct)?.images?.edges?.[0]?.node.id,
      },
      handle: (product as GQLProduct)?.handle,
      id: (product as GQLProduct)?.id,
      media: (product as GQLProduct)?.images.edges.map(({ node }) => ({
        alt: node.altText,
        height: node.height,
        width: node.width,
        src: node.url,
        id: node.id,
      })),
      price: parseFloat((product as GQLProduct)?.priceRange.minVariantPrice.amount),
      price_min: parseFloat((product as GQLProduct)?.priceRange.minVariantPrice.amount),
      price_max: parseFloat((product as GQLProduct)?.priceRange.maxVariantPrice.amount),
      tags: (product as GQLProduct)?.tags,
      title: (product as GQLProduct)?.title,
      variants: (product as GQLProduct)?.variants?.edges?.map(({ node }) => ({
        title: stripColourFromVariantTitle(node.title),
        available: node.availableForSale,
        compare_at_price: node.compareAtPriceV2?.amount ? parseFloat(node.compareAtPriceV2.amount) : null,
        currentlyNotInStock: node.currentlyNotInStock,
        id: node.id,
        price: parseFloat(node.priceV2.amount),
      })),
    };
  };

  return {
    normaliseProduct,
    normaliseElasticProduct,
  };
};
